import React from 'react';
import { Button, Table, Input, Icon, Message, Modal, SelectHierarchy, Form, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { fieldsOptions } from './datas';
import css from './imports-steps.module.css';
import { fetcher } from '@weezevent/weezjs-core';
import LoadingCard from '../../../../components/loadingCard';
import AttendeeImportProcess from '../model';

import { PollingImportRequest } from './utils';
import { isEqual, cloneDeep } from 'lodash';

export const DataStep = ({ event, form, setForm, onCardDone, onDone, openModal }) => {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [importReady, setImportReady] = React.useState(false);
    const [originalHeaderMapping, setOriginalHeaderMapping] = React.useState(form?.requestDatas ? cloneDeep(form.requestDatas['_details']) : {});
    const [shouldDisplayConfirmationModal, setShouldDisplayConfirmationModal] = React.useState(false);

    const _onDone = React.useCallback(
        requestDatas => {
            onDone(requestDatas, onCardDone);
        },
        [onCardDone, onDone]
    );

    const headersLabels = React.useMemo(() => {
        return fieldsOptions.map(fd => fd.value);
    }, [fieldsOptions]);

    const headersOptions = React.useMemo(() => {
        return fieldsOptions.map(fd => ({ ...fd, text: t(fd.text) }));
    }, [fieldsOptions]);

    const handleChange = React.useCallback(
        (value, index) => {
            let datas = { ...form.requestDatas };
            datas._details.columns[index].target = value;
            setForm('requestDatas', datas);
        },
        [form, setForm]
    );

    const errors = React.useMemo(() => {
        if (!importReady) {
            return [];
        }

        const requiredFields = ['ticket_type', 'zone'];
        const { duplicates } = form.requestDatas._details.columns.reduce(
            (acc, element) => {
                acc[element.target] = (acc[element.target] || 0) + 1;
                if (element.target && acc[element.target] > 1) {
                    acc.duplicates.push(element.target);
                }
                return acc;
            },
            { duplicates: [] }
        );

        if (!event.is_seated) {
            return { missingFields: form.requestDatas._details.columns.filter(cl => !cl.target), duplicateFields: duplicates };
        }
        return { missingFields: form.requestDatas._details.columns.filter(cl => requiredFields.includes(cl.header) && !cl.target), duplicateFields: duplicates };
    }, [event, form, importReady]);

    const handlePatchImportRequest = (fromConfirmationModal = false) => {
        if (errors.length) {
            return;
        }

        const payload = { data: { step_name: 'update_header' } };
        const hasDetailsChange = !isEqual(originalHeaderMapping, form.requestDatas['_details']);
        if (hasDetailsChange) {
            payload['data']['details'] = form.requestDatas['_details'];
        }

        let isCodeBarreInData = form.requestDatas['_details']['columns'].some(obj => {
            return obj.target === 'barcode';
        });

        if (fromConfirmationModal || isCodeBarreInData) {
            setShouldDisplayConfirmationModal(false);
            fetcher
                .patch(form.importRequest.url, payload)
                .then(({ response, status }) => {
                    const onSuccessHeaderMapping = resp => {
                        let requestDatas = new AttendeeImportProcess(resp);
                        _onDone(requestDatas);
                    };

                    const onSuccess = data => {
                        onSuccessHeaderMapping(data);
                        setImportReady(true);
                    };
                    const onError = () => {
                        toast.error(t('ticket.common.toast_error'));
                    };
                    if (status === 200) {
                        if (hasDetailsChange) {
                            // If details is not empty, it means that user has changed header mapping
                            // Thus, after patching header mapping, gofast has to reprocess validation step
                            // A polling step is then required
                            setImportReady(false);
                            PollingImportRequest(fetcher, form, onSuccess, onError);
                        } else {
                            // Details has not changed, no need to reprocess validation step
                            onSuccessHeaderMapping(response);
                        }
                    }
                })
                .catch(() => {
                    toast.error(t('ticket.common.toast_error'));
                });
        } else {
            setShouldDisplayConfirmationModal(true);
        }
    };

    const headerColumnDetails = React.useMemo(() => {
        return form.requestDatas?._details?.columns;
    }, [form]);

    React.useEffect(() => {
        if (!form.importRequest) return;

        function onSuccess(response) {
            setForm('requestDatas', new AttendeeImportProcess(response));
            setImportReady(true);
            setOriginalHeaderMapping(cloneDeep(response?.details));
        }

        function onError() {
            toast.error(t('ticket.common.toast_error'));
        }

        if (!Object.keys(form.requestDatas).length) {
            PollingImportRequest(fetcher, form, onSuccess, onError);
            return;
        }

        if (form.requestDatas._status === 'valid') {
            setImportReady(true);
        }
    }, []);

    const hasRateIdName = React.useMemo(() => {
        const valuesToCheck = ['rate_id', 'ticket_type'];
        if (form.requestDatas._details && form.requestDatas._details.columns) {
            return form.requestDatas._details.columns.some(obj => valuesToCheck.includes(obj['target']));
        }
        return false;
    }, [form]);

    const shouldDisabledButton = React.useMemo(() => {
        return errors?.duplicateFields?.length || !hasRateIdName;
    }, [form]);

    return (
        <>
            {shouldDisplayConfirmationModal && (
                <Modal
                    size="large"
                    title={t('ticket.attendees.imports.modal.title_codes_barres')}
                    subtitle={t('ticket.attendees.imports.modal.sub_title_codes_barres')}
                    open={shouldDisplayConfirmationModal}
                    onClose={() => {
                        setShouldDisplayConfirmationModal(false);
                    }}
                    submitButton={
                        <Button color="white" labelPostion="left" onClick={() => handlePatchImportRequest(true)}>
                            {t('ticket.common.cta.continue')}
                        </Button>
                    }
                    cancelButton={
                        <Button
                            onClick={() => {
                                setShouldDisplayConfirmationModal(false);
                            }}
                        >
                            {t(`ticket.common.action.back`)}
                        </Button>
                    }
                >
                    <Message type="warning" title={t('ticket.attendees.imports.modal.title_message_info_codes_barres')}>
                        {t('ticket.attendees.imports.modal.description_message_info_codes_barres')}
                    </Message>
                </Modal>
            )}

            {importReady && !hasRateIdName && (
                <Message type="danger" title={t('ticket.attendees.imports.modal.title_message_warning_rate_name')}>
                    {t('ticket.attendees.imports.modal.description_message_warning_rate')}
                    <ul>
                        <li>{t('ticket.attendees.imports.modal.description_message_warning_rate_id')}</li>
                    </ul>
                </Message>
            )}

            <div className={css['step-body']}>
                <LoadingCard loading={!importReady}>
                    <ImportErrorMessage values={fieldsOptions} errors={errors} />
                    <Table useNew className={css['mapping-table']}>
                        <Table.THead>
                            <Table.Tr>
                                <th>{t('ticket.attendees.imports.steps.data.table.columns')}</th>
                                <th /> {/* Empty header for icon display */}
                                <th>{t('ticket.attendees.imports.steps.data.table.fields')}</th>
                                {/* Field hidden waiting for product decision */}
                                {/* <th>{t(`ticket.attendees.imports.steps.data.table.examples`)}</th> */}
                            </Table.Tr>
                        </Table.THead>
                        {headerColumnDetails &&
                            headerColumnDetails.map((it, index) => (
                                <TableItem key={it.id} index={index} item={it} errors={errors} options={headersOptions} labels={headersLabels} handleChange={handleChange} />
                            ))}
                    </Table>
                    <div className={css['step-footer']}>
                        <Button
                            className={css['step-cta']}
                            primary
                            label={t('ticket.common.action.continue')}
                            onClick={() => handlePatchImportRequest(false)}
                            disabled={shouldDisabledButton}
                        />
                        <Button label={t('ticket.attendees.imports.steps.data.cta')} onClick={openModal} />
                    </div>
                </LoadingCard>
            </div>
        </>
    );
};

const TableItem = ({ item, index, errors, options, labels, handleChange }) => {
    const { t } = useTranslation();

    // Field hidden waiting for product decision
    // const itemExample = React.useMemo(() => {
    //     return options.find(it => it.value === item.target)?.example || '...';
    // }, [options, item.target]);

    const duplicateErrors = React.useMemo(() => {
        return errors.duplicateFields.some(err => err === item.target);
    }, [errors, item.target]);

    return (
        <Table.Tr className={css['mapping-row']}>
            <Table.Td className={css['mapping-table-large']}>
                <Input disabled value={item.header} />
            </Table.Td>
            <Table.Td className={css['mapping-table-small']}>
                <Icon name="arrow-right" circular />
            </Table.Td>
            <Table.Td className={css['mapping-table-large']}>
                <SelectHierarchy
                    options={options}
                    value={item.target && labels.includes(item.target) ? item.target : null}
                    errors={duplicateErrors}
                    placeholder={t('ticket.attendees.imports.steps.data.table.select')}
                    onChange={value => handleChange(value, index)}
                    onClear={() => handleChange(null, index)}
                    chevron
                />
                {duplicateErrors && <Form.FieldErrors errors={[t('ticket.attendees.imports.steps.data.errors.duplicates')]} />}
            </Table.Td>
            {/* Field hidden waiting for product decision */}
            {/* <Table.Td className={css['mapping-table-large']}>
                <div className={css['mapping-table-details']}>{itemExample}</div>
            </Table.Td> */}
        </Table.Tr>
    );
};

const ImportErrorMessage = React.memo(({ values, errors }) => {
    const { t } = useTranslation();

    const missingFields = React.useMemo(() => {
        if (!errors?.missingFields.length) {
            return [];
        }
        return errors.missingFields.map(cl => values.find(vl => vl.value === cl.header)).filter(Boolean);
    }, [values, errors]);

    if (!missingFields.length) {
        return null;
    }
    return (
        <Message type="danger" title={t('ticket.attendees.imports.steps.data.errors.fields.title')}>
            <div className={css['mapping-errors']}>
                <div className={css['mapping-errors-message']}>{t('ticket.attendees.imports.steps.data.errors.fields.message')}</div>
                {missingFields.map((fld, i) => (
                    <li key={i}>{fld.text}</li>
                ))}
            </div>
        </Message>
    );
});
